import React from 'react';
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTranslations } from "@/hooks/useTranslations";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Channel } from './types';
import { cn } from '@/lib/utils';

type Contact = {
  id: string;
  name: string;
  avatar_url: string | null;
};

type ConversationListProps = {
  channels: Channel[];
  directContacts: Contact[];
  loading: boolean;
  selectedChannelId: string | null;
  selectedUserId: string | null;
  unreadConversations: Set<string>;
  getConversationId: (type: 'channel' | 'dm', id: string) => string;
  onSelectChannel: (channel: Channel | null) => void;
  onSelectUser: (userId: string | null) => void;
  onOpenCreateChannel: () => void;
  onOpenStudentList: () => void;
};

export const ConversationList = ({ 
  channels,
  directContacts,
  loading,
  selectedChannelId,
  selectedUserId,
  unreadConversations,
  getConversationId,
  onSelectChannel,
  onSelectUser,
}: ConversationListProps) => {
  const { t } = useTranslations('messages');
  
  if (loading) {
    return (
      <div className="p-6 text-sm text-center text-muted-foreground">{t('loading')}</div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Channels */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="px-4 py-2.5">
          <h3 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">{t('channels')}</h3>
        </div>

        <ScrollArea className="flex-1">
          <div className="px-2 space-y-0.5">
            {channels.map((channel) => {
              const conversationId = getConversationId('channel', channel.id);
              const isUnread = unreadConversations.has(conversationId);
              const isSelected = selectedChannelId === channel.id;
              return (
                <div
                  key={channel.id}
                  className={cn(
                    "flex items-center gap-2 w-full px-3 py-2 rounded-lg cursor-pointer text-sm transition-all duration-150",
                    isSelected 
                      ? "bg-primary/10 text-primary font-medium shadow-sm" 
                      : "hover:bg-accent/60 text-foreground"
                  )}
                  onClick={() => onSelectChannel(isSelected ? null : channel)}
                >
                  <span className="text-muted-foreground font-medium">#</span>
                  {isUnread && <span className="h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />}
                  <span className={cn("truncate", isUnread && "font-semibold")}>
                    {channel.name}
                  </span>
                </div>
              );
            })}
            {channels.length === 0 && (
              <div className="px-3 py-3 text-xs text-muted-foreground text-center">
                {t('noChannels')}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Direct Messages */}
      <div className="flex-1 min-h-0 border-t border-border flex flex-col">
        <div className="px-4 py-2.5">
          <h3 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">{t('directMessages')}</h3>
        </div>

        <ScrollArea className="flex-1">
          <div className="px-2 space-y-0.5">
            {directContacts.map((contact) => {
              const conversationId = getConversationId('dm', contact.id);
              const isUnread = unreadConversations.has(conversationId);
              const isSelected = selectedUserId === contact.id;
              return (
                <div
                  key={contact.id}
                  className={cn(
                    "flex items-center gap-2.5 w-full px-3 py-2 rounded-lg cursor-pointer text-sm transition-all duration-150",
                    isSelected 
                      ? "bg-primary/10 text-primary font-medium shadow-sm" 
                      : "hover:bg-accent/60 text-foreground"
                  )}
                  onClick={() => onSelectUser(isSelected ? null : contact.id)}
                >
                  <Avatar className="h-7 w-7 flex-shrink-0">
                    <AvatarImage src={contact.avatar_url || ''} />
                    <AvatarFallback className="text-[10px] bg-muted">
                      {(contact.name?.charAt(0) || '?').toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex items-center gap-1.5 min-w-0">
                    {isUnread && <span className="h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />}
                    <span className={cn("truncate", isUnread && "font-semibold")}>
                      {contact.name}
                    </span>
                  </div>
                </div>
              );
            })}
            {directContacts.length === 0 && (
              <div className="px-3 py-3 text-xs text-muted-foreground text-center">
                {t('noDirectMessages')}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};
